import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
  Item,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@design-system';
import {
  CheckoutLayout,
  useCheckoutLeaveConfirmation,
  useStartTrialStep,
  type BillingInterval,
} from '@/features/advisor-onboarding';
import { SubscriptionStatusContent } from '@client/components/advisor-account-seats';

import { Check, Clock, Sparkles, TrendingUp, Shield, Loader2, LucideIcon } from 'lucide-react';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';

import { BookDemoPersonaModal } from './book-demo-persona-modal';
import { BookDemoVideoModal } from './book-demo-video-modal';

const FEATURES = [
  {
    icon: Clock,
    title: 'Create LTC plans in minutes',
    description: 'Start impressing your clients today',
  },
  {
    icon: TrendingUp,
    title: 'Analyze any type of LTC product',
    description: 'Plus instantly quote from leading carriers',
  },
  {
    icon: Shield,
    title: 'HIPAA compliant & SOC 2 certified',
    description: 'Enterprise-grade security for your clients',
  },
] as const;

const TRIAL_BENEFITS = [
  'Create unlimited LTC plans for up to 100 clients',
  'Full access to AI-powered recommendations',
  'Live customer support',
] as const;

interface FeatureItemProps {
  icon: LucideIcon;
  title: string;
  description: string;
}

function FeatureItem({ icon: Icon, title, description }: FeatureItemProps) {
  return (
    <Item className="border-border shadow-xs items-center rounded-xl border p-4">
      <ItemMedia className="bg-primary/10 translate-y-0! flex size-12 shrink-0 items-center justify-center rounded-full">
        <Icon className="text-primary size-5" />
      </ItemMedia>
      <ItemContent>
        <ItemTitle className="text-sm font-semibold text-gray-950 sm:text-base">{title}</ItemTitle>
        <ItemDescription className="text-foreground text-xs sm:text-sm">
          {description}
        </ItemDescription>
      </ItemContent>
    </Item>
  );
}

interface AdvisorOnboardingStartTrialStepProps {
  advisorId: string;
}

export function AdvisorOnboardingStartTrialStep({
  advisorId,
}: AdvisorOnboardingStartTrialStepProps) {
  const navigate = useNavigate();
  const {
    viewState,
    selectedInterval,
    setSelectedInterval,
    isPending,
    pendingPlanType,
    isCheckoutLoading,
    clientSecret,
    hasActiveSubscription,
    hasPendingInvitation,
    hasPendingTeamInvitation,
    isInCheckoutFlow,
    isFetching,
    checkoutContent,
    trialDetailFormatted,
    handleStartTrial,
    handleBackToPlans,
  } = useStartTrialStep({ advisorId });

  const { flags } = useFeatureFlags();
  const enableLeaveAndBookDemo = flags.checkoutLeaveAndBookDemo ?? false;

  const isCheckoutView = viewState === 'checkout';
  const [bookDemoPersonaOpen, setBookDemoPersonaOpen] = useState(false);
  const [bookDemoVideoOpen, setBookDemoVideoOpen] = useState(false);
  const { leaveModalOpen, leaveModalVariant, onLeave, onStay, onContinue } =
    useCheckoutLeaveConfirmation({
      enabled: isCheckoutView && enableLeaveAndBookDemo,
      onConfirmLeave: handleBackToPlans,
      pauseInactivityWhen: enableLeaveAndBookDemo && (bookDemoPersonaOpen || bookDemoVideoOpen),
      pauseInactivityWhenFocusedInIframe: isCheckoutView && enableLeaveAndBookDemo,
    });

  const handleBookDemo = () => {
    onStay();
    setBookDemoPersonaOpen(true);
  };

  const handlePersonaBookCall = () => {
    setBookDemoPersonaOpen(false);
    setBookDemoVideoOpen(true);
  };

  const handleVideoBack = () => {
    setBookDemoVideoOpen(false);
    setBookDemoPersonaOpen(true);
  };

  if (isFetching) {
    return (
      <div className="flex flex-col items-center justify-center p-4 sm:p-6 md:p-8">
        <div className="flex items-center gap-3">
          <Loader2 className="text-primary size-5 animate-spin" />
          <p className="text-foreground text-sm">Loading subscription information...</p>
        </div>
      </div>
    );
  }

  if (viewState === 'checkout') {
    return (
      <>
        <CheckoutLayout
          content={checkoutContent}
          trialDetailFormatted={trialDetailFormatted}
          onBack={handleBackToPlans}
          isLoading={isCheckoutLoading}
          clientSecret={clientSecret}
        />
        {enableLeaveAndBookDemo && (
          <>
            <AlertDialog open={leaveModalOpen}>
              <AlertDialogContent
                className={cn(leaveModalVariant === 'inactivity' && 'z-51')}
              >
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {leaveModalVariant === 'back'
                      ? 'Have questions about joining Waterlily?'
                      : 'Still there?'}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {leaveModalVariant === 'back'
                      ? "We're happy to give you a demo"
                      : 'Complete your payment to continue.'}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="sm:justify-end">
                  {leaveModalVariant === 'back' ? (
                    <>
                      <AlertDialogCancel onClick={onLeave}>Leave</AlertDialogCancel>
                      <AlertDialogAction onClick={handleBookDemo}>Book a demo</AlertDialogAction>
                    </>
                  ) : (
                    <AlertDialogAction onClick={onContinue}>Continue</AlertDialogAction>
                  )}
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <BookDemoPersonaModal
              open={bookDemoPersonaOpen}
              onOpenChange={setBookDemoPersonaOpen}
              onBookCall={handlePersonaBookCall}
            />
            <BookDemoVideoModal
              open={bookDemoVideoOpen}
              onOpenChange={setBookDemoVideoOpen}
              onBack={handleVideoBack}
            />
          </>
        )}
      </>
    );
  }

  if ((hasActiveSubscription || hasPendingInvitation) && !isInCheckoutFlow) {
    return (
      <div className="flex flex-col items-center px-4 sm:px-6 md:px-8">
        <div className="w-full max-w-3xl space-y-6">
          <div className="space-y-2 text-center sm:space-y-3">
            <h2 className="flex items-center justify-center gap-2 text-xl font-semibold text-gray-950 sm:text-2xl">
              {!hasActiveSubscription && hasPendingTeamInvitation
                ? 'Team Invitation'
                : 'Subscription Status'}
              {hasActiveSubscription && (
                <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-green-100 sm:size-7">
                  <Check className="size-4 text-green-700 sm:size-5" />
                </div>
              )}
            </h2>
            <p className="text-foreground mt-2 text-sm sm:text-base">
              {hasActiveSubscription
                ? 'You have an active subscription. View your subscription details below.'
                : hasPendingTeamInvitation
                  ? 'You have been invited to join a team. Please accept or decline the invitation below.'
                  : 'You have a pending subscription invitation. Please accept or decline it below.'}
            </p>
          </div>
          <SubscriptionStatusContent
            advisorId={advisorId}
            variant="compact"
            hideManagementActions
            onInvitationAccepted={() => navigate(`/advisors/${advisorId}/onboarding/activate-seat`)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center p-4 sm:p-6 md:p-8">
      <div className="w-full max-w-5xl space-y-6 sm:space-y-10">
        <div className="space-y-2 text-center sm:space-y-3">
          <h2 className="text-xl font-semibold text-gray-950 sm:text-2xl">
            Create your first LTC plan in ~5 minutes
          </h2>
          <p className="text-foreground mt-2 text-sm sm:text-base">
            You're almost there. Choose how you'd like to get started.
          </p>
        </div>

        <div className="grid gap-6 sm:gap-8 lg:grid-cols-2 lg:gap-12">
          <div className="space-y-6">
            <div className="space-y-2 text-center md:text-left">
              <h3 className="text-lg font-semibold text-gray-950 sm:text-xl">
                What you'll get with Waterlily
              </h3>
              <p className="text-foreground text-sm sm:text-base">
                The most advanced AI-driven personalized LTC planning
              </p>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {FEATURES.map(feature => (
                <FeatureItem
                  key={feature.title}
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                />
              ))}
            </div>

            <div className="text-foreground flex flex-wrap items-center justify-center gap-4 text-xs font-medium sm:gap-8 sm:text-sm">
              <div className="flex items-center gap-2">
                <Check className="text-primary size-4" />
                <span>Full access</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="text-primary size-4" />
                <span>Cancel anytime</span>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex justify-center">
              <Tabs
                value={selectedInterval}
                onValueChange={v => v && setSelectedInterval(v as BillingInterval)}
              >
                <TabsList className="h-9 w-fit rounded-full border border-gray-200 bg-gray-50 p-0.5">
                  <TabsTrigger
                    value="monthly"
                    className="min-w-16 rounded-full px-2.5 py-1.5 text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  >
                    Monthly
                  </TabsTrigger>
                  <TabsTrigger
                    value="annual"
                    className="min-w-16 rounded-full px-2.5 py-1.5 text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  >
                    Annual
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            <Card className="border-primary relative overflow-hidden border-2 transition-all duration-200">
              <CardHeader className="gap-0">
                <Badge
                  variant="default"
                  className="flex w-fit items-center gap-1.5"
                >
                  {selectedInterval === 'annual' ? (
                    <>
                      <Sparkles className="text-primary size-3.5" />
                      Best value
                    </>
                  ) : (
                    'Cancel anytime'
                  )}
                </Badge>
                <CardTitle className="mb-1 mt-4 text-xl font-bold sm:text-2xl">
                  Start your 14-day free trial
                </CardTitle>
                <CardDescription className="text-foreground text-sm sm:text-base">
                  Get access immediately for free. Cancel any time.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                {checkoutContent.priceAfterTrial && (
                  <p className="text-muted-foreground text-xs">
                    Billed only after your 14-day trial:{' '}
                    <span className="font-semibold text-gray-950">
                      {checkoutContent.priceAfterTrial}
                    </span>
                  </p>
                )}

                <ul className="space-y-3">
                  {TRIAL_BENEFITS.map(benefit => (
                    <li
                      key={benefit}
                      className="text-foreground flex items-center gap-3 text-sm"
                    >
                      <div className="grid size-5 shrink-0 place-items-center rounded-full bg-green-50">
                        <Check className="size-3.5 text-green-800" />
                      </div>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  size="lg"
                  disabled={isPending}
                  onClick={handleStartTrial}
                  className="w-full"
                >
                  {pendingPlanType ? (
                    <>
                      <Loader2 className="animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Get started for free'
                  )}
                </Button>

                <p className="text-muted-foreground text-center text-xs">
                  We'll remind you a few days before your trial ends. Cancel anytime.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
